<script>
    import { onMount } from "svelte";
    import debounce from "lodash/debounce";

    import {
        PageHead,
        PageLoading,
        SvelteTable,
        ErrorModal,
        Loader,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        goBack,
        setInputFilter,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";

    import { SEARCH } from "../../Utils/utilConstants.svelte";

    import { selectedPaymentCsvFile } from "../../stores";
    import { _getDisplayDate } from "../PatientEncounter/helpers/encounterHelper.svelte";

    let isLoadingCSVFile = false,
        csvFileArr = [],
        error;

    let isMatchModalOpen = false,
        isConfirmMatch = false,
        isSearchingEstimateOrInvoice = false,
        estimateOrInvoiceResult = { id: 0 },
        isValidEstimateOrInvoice = true,
        isMatching = false,
        confirmText = "",
        selectedResult = {
            manualTransactionId: null,
            manualRelTo: 0,
            manualRelId: undefined,
            paidBy: 0,
        };

    const tempPaymentCsvFile = $selectedPaymentCsvFile;

    $: matchBtnDisabled =
        !selectedResult.manualTransactionId ||
        selectedResult.manualRelTo < 0 ||
        !selectedResult.manualRelId ||
        isSearchingEstimateOrInvoice ||
        !estimateOrInvoiceResult.id ||
        (estimateOrInvoiceResult.id && estimateOrInvoiceResult.status == 9);

    $: isEstimate = selectedResult.manualRelTo == 0;

    const columns = [
        {
            key: "key",
            title: "Transaction description",
            headerClass: "text-left pl-4 w-5/12 rounded-tl-xl",
            class: "text-left p-4",
            value: (v) => `${v.key} on ${_getDisplayDate(v.date)}`,
        },
        {
            key: "result",
            title: "Results",
            headerClass: "text-left pl-4 w-5/12",
            class: "text-left p-4",
            value: (v) => v.result ?? "",
        },
        {
            key: "action",
            title: "",
            headerClass: "text-right pl-4 w-2/12 rounded-tr-xl",
            class: "text-right p-4",
            renderValue: (v) => _renderActionBtn(v.result, v.row),
        },
    ];

    onMount(() => {
        if ($selectedPaymentCsvFile.content) postCSVFile();
        else goBack();
    });

    const postCSVFile = () => {
        isLoadingCSVFile = true;

        apiMethods.postMethod
            .postPaymentEFTCSV({ csvData: tempPaymentCsvFile.content })
            .then((res) => {
                if (res.error) showError(res.error);
                else handleCSVFile(res);
            })
            .then(() => ($selectedPaymentCsvFile = {}))
            .then(() => (isLoadingCSVFile = false))
            .then(() => _updateInputField());
    };

    const handleCSVFile = (res) => {
        csvFileArr = res.filter(r => r.id != null);
        
        for(let i = 0; i < csvFileArr.length; i++){
            csvFileArr[i] = {
                ...csvFileArr[i],
                row: i+1,
                isMatchNeeded: _checkShowMatchBtn(csvFileArr[i].result)
            }
        }
    };

    const searchEstimateOrInvoice = () => {
        if (
            !selectedResult.manualRelId ||
            !/^[0-9-\s]+$/i.test(selectedResult.manualRelId)
        )
            return;

        isSearchingEstimateOrInvoice = true;
        isValidEstimateOrInvoice = true;
        estimateOrInvoiceResult = { id: 0 };
        confirmText = "";

        handleFetchingEstimateOrInvoice();
    };

    const handleFetchingEstimateOrInvoice = debounce(() => {
        if (isEstimate) getEstimateById();
        else getInvoiceById();
    }, SEARCH.DEBOUNCE_TIME);

    const getEstimateById = async () => {
        await apiMethods.getMethod
            .getEstimateById(selectedResult.manualRelId)
            .then((res) => {
                if (res.id) estimateOrInvoiceResult = { ...res };
                else isValidEstimateOrInvoice = false;
            })
            .catch((err) => showException(err));

        isSearchingEstimateOrInvoice = false;
    };

    const getInvoiceById = async () => {
        await apiMethods.getMethod
            .getInvoiceById(selectedResult.manualRelId)
            .then((res) => {
                if (res.id) estimateOrInvoiceResult = { ...res };
                else isValidEstimateOrInvoice = false;
            })
            .catch((err) => showException(err));

        isSearchingEstimateOrInvoice = false;
    };

    const handleClickCell = (rowData, key) => {
        console.log("handle click cell:", rowData);
        if (
            key == "action" &&
            rowData.isMatchNeeded &&
            rowData.id
        ) {
            selectedResult.manualTransactionId = rowData.id;

            openMatchModal();
        }
    };

    const openMatchModal = () => {
        isMatchModalOpen = true;
    };

    const confirmMatch = () => {
        isConfirmMatch = true;

        confirmText = `Assign this payment from ${
            selectedResult.paidBy == 0 ? "patient" : "health fund"
        } to ${isEstimate ? "quote" : "invoice"} ${
            selectedResult.manualRelId
        }?`;
    };

    const closeMatchModal = () => {
        isMatchModalOpen = false;
        isConfirmMatch = false;
        isValidEstimateOrInvoice = true;

        selectedResult = {
            manualTransactionId: null,
            manualRelTo: 0,
            manualRelId: undefined,
            paidBy: 0,
        };
        estimateOrInvoiceResult = { id: 0 };
    };

    const confirmMatchProcess = async () => {
        isMatching = true;

        await apiMethods.postMethod
            .postPaymentEFTCSV({
                csvData: tempPaymentCsvFile.content,
                manualTransactionId: selectedResult.manualTransactionId,
                manualRelTo: selectedResult.manualRelTo,
                manualRelId: selectedResult.manualRelId,
            })
            .then((res) => {
                if (!res || res.length <= 0) showError(res?.error);
                else {
                    //identify the transaction and update error and result
                    csvFileArr.forEach((item) => {
                        if (item.id == selectedResult.manualTransactionId) {
                            //do something
                            item.result = res[0].error ?? res[0].result;
                        }
                    });

                    csvFileArr = [...csvFileArr];
                }
            });

        isMatching = false;

        closeMatchModal();
    };

    // HELPER FUNCTIONS

    const _renderActionBtn = (value, row) => {
        if (!_checkShowMatchBtn(value)) return "";

        return `<button
                    class="match-${row} btn btn-sm btn-secondary text-white text-sm flex-none border-none w-auto min-w-[70px]"
                >
                    Match Manually
                <button>`;
    };

    const _checkShowMatchBtn = (value) => {
        return (
            !value || value.toLowerCase() == "empty description" ||
            value.toLowerCase() == "unable to find billing item"
        );
    };

    const _updateInputField = () => {
        setInputFilter(
            document.getElementById("estimateOrInvoiceIdField"),
            function (value) {
                return value ? /^[0-9-\s]+$/i.test(value) : true;
            },
            "Must be an integer",
        );
    };
</script>

<PageHead title="View payment CSV results" />

{#if isLoadingCSVFile}
    <PageLoading />
{:else if csvFileArr.length}
    <div class="flex flex-col flex-1 mt-4">
        <div class="relative flex-1">
            <div class="overflow-y-auto absolute inset-0">
                <SvelteTable
                    {columns}
                    rows={csvFileArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(row, key);
                    }}
                />
            </div>
        </div>
    </div>
{/if}

<div class={isMatchModalOpen ? "modal modal-open sm:modal-middle" : "modal"}>
    <div class="modal-box">
        <h3 class="font-medium text-lg">Manually Match Payment:</h3>
        {#if isConfirmMatch}
            <div>{confirmText}</div>
        {:else}
            <div class="flex flex-col gap-y-4">
                <div class="flex items-end gap-x-2">
                    <p class="w-36">Type:</p>
                    <select
                        class="input-sm w-48"
                        bind:value={selectedResult.manualRelTo}
                        on:change|preventDefault={searchEstimateOrInvoice}
                    >
                        <option value={Number(0)} selected>Quote</option>
                        <option value={Number(1)}>Invoice</option>
                    </select>
                </div>
                <div class="flex gap-x-2">
                    <p class="w-36">
                        {isEstimate ? "Quote" : "Invoice"} ID:
                    </p>
                    <input
                        type="text"
                        id="estimateOrInvoiceIdField"
                        class="input-sm w-48"
                        bind:value={selectedResult.manualRelId}
                        on:input|preventDefault={searchEstimateOrInvoice}
                    />
                    {#if isSearchingEstimateOrInvoice}
                        <Loader />
                    {/if}
                </div>
                {#if !isValidEstimateOrInvoice}
                    <p class="text-error">
                        Unable to find {isEstimate ? "Quote" : "Invoice"}
                    </p>
                {:else if !isSearchingEstimateOrInvoice && estimateOrInvoiceResult.id}
                    {#if estimateOrInvoiceResult.status == 9}
                        <p class="text-error">
                            {isEstimate ? "Quote" : "Invoice"} is CANCELLED
                        </p>
                    {:else}
                        <div class="flex flex-col gap-y-2 pl-8">
                            <div class="flex gap-x-2">
                                <p class="w-36">Billing Name:</p>
                                <p>
                                    {estimateOrInvoiceResult.billingDetails
                                        ?.name}
                                </p>
                            </div>
                            <div class="flex gap-x-2">
                                <p class="w-36">
                                    {isEstimate ? "Expire" : "Due"} Date:
                                </p>
                                <p>
                                    {isEstimate
                                        ? estimateOrInvoiceResult.expireDate?.split(
                                              "T",
                                          )[0]
                                        : estimateOrInvoiceResult.dueDate?.split(
                                              "T",
                                          )[0]}
                                </p>
                            </div>
                        </div>
                    {/if}
                {/if}
                <div class="flex items-end gap-x-2">
                    <p class="w-36">Paid By:</p>
                    <select
                        class="input-sm w-48"
                        bind:value={selectedResult.paidBy}
                    >
                        <option value={Number(0)} selected>Patient</option>
                        <option value={Number(2)}>Health Fund</option>
                    </select>
                </div>
            </div>
        {/if}
        <div class="modal-action">
            <div class="flex gap-x-4">
                <button
                    class="btn btn-error text-white btn-sm border-none w-24"
                    on:click={closeMatchModal}>Close</button
                >

                {#if isConfirmMatch}
                    <button
                        class={`btn btn-secondary text-white btn-sm border-none ${
                            isMatching ? "loading w-36" : " w-24"
                        }`}
                        on:click={confirmMatchProcess}
                        disabled={isMatching}
                    >
                        {isMatching ? "Uploading" : "Confirm"}
                    </button>
                {:else}
                    <button
                        class="btn btn-secondary text-white btn-sm border-none w-24"
                        on:click={confirmMatch}
                        disabled={matchBtnDisabled}
                    >
                        Match
                    </button>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
    select {
        outline: 0;
        border-width: 0 0 2px;
        border-color: #818e9b;
    }

    input {
        outline: 0;
        border-width: 0 0 2px;
        border-color: #818e9b;
        color: #270053;
    }

    input:focus {
        border-color: #270053;
    }
</style>
